import { doubleMetaphone } from 'double-metaphone';
import { dictionary } from '../../assets/data/dictionary';
import { ITranscriptCorrectionsMap } from '../interfaces/itranscript-corrections-map.ts';
import { ITranscriptCorrection } from '../interfaces/itranscript-correction';
import { IWords } from '../interfaces/iwords';
import { inject } from '@angular/core';
import { LoggerService } from '../services/logger/logger.service';
import { localeLowerCase } from './locale-case.util';

//Scripts written without spaces between words
const NO_SPACE_SCRIPT: RegExp = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Thai}\p{Script=Lao}\p{Script=Khmer}\p{Script=Myanmar}]/u;

//Replaces each occurrence of term that starts and ends on a word boundary. A term the segmenter splits into
//several words still matches (e.g. a katakana brand name, キスカリ → キス|カリ), but never inside another word.
function replaceOnWordBoundaries(text: string, term: string, replacement: string, getWords: () => Intl.SegmentData[], locale: string): { text: string, count: number } {
    //Lowercasing can change the length of some characters (e.g. İ), which would shift the positions,
    //so only those characters keep their original form
    const lowerKeepingLength = (value: string): string => {
        const lower: string = localeLowerCase(value, locale);
        if (lower.length === value.length) {
            return lower;
        }
        return Array.from(value, (char) => {
            const lowerChar: string = localeLowerCase(char, locale);
            return lowerChar.length === char.length ? lowerChar : char;
        }).join('');
    };
    const searchText: string = lowerKeepingLength(text);
    const searchTerm: string = lowerKeepingLength(term);

    let index: number = searchText.indexOf(searchTerm);

    //Most terms don't occur in the transcript, so the text is only segmented when one does
    if (index === -1) {
        return { text, count: 0 };
    }

    const boundaries = new Set<number>();
    for (const word of getWords()) {
        boundaries.add(word.index);
        boundaries.add(word.index + word.segment.length);
    }

    let result: string = '';
    let count: number = 0;
    let copiedUpTo: number = 0;

    while (index !== -1) {
        const end: number = index + searchTerm.length;
        if (boundaries.has(index) && boundaries.has(end)) {
            result += text.slice(copiedUpTo, index) + replacement;
            copiedUpTo = end;
            count++;
            index = searchText.indexOf(searchTerm, end);
        } else {
            index = searchText.indexOf(searchTerm, index + 1);
        }
    }

    return { text: result + text.slice(copiedUpTo), count };
}

export class correctedTranscript {
    constructor(
        public transcript: string,
        public changes: any[][]
    ) { }
}

export class TranscriptCorrections {
    private corrections: ITranscriptCorrectionsMap;

    constructor() {
        this.corrections = {};
    }


    public addTranscriptChangesLogs(newChangesList: any[]): void {
        newChangesList.forEach(subArray => {
            const [correctWord, change, ...values] = subArray;
            if (correctWord) {
                if (!this.corrections[correctWord]) {
                    this.corrections[correctWord] = {};
                }

                if (!this.corrections[correctWord][change]) {
                    this.corrections[correctWord][change] = values;
                } else {
                    const existingValues = this.corrections[correctWord][change];
                    const countIndex = 3;

                    if (existingValues.length > countIndex) {
                        existingValues[countIndex] += 1;
                    } else {
                        existingValues[countIndex] = 1;
                    }
                }
            }
        });
    }

    public getCorrections(): ITranscriptCorrection[] {

        let result: ITranscriptCorrection[] = [];

        Object.entries(this.corrections).forEach(([key, innerMap]) => {
            Object.entries(innerMap).forEach(([innerKey, values]) => {

                result.push({
                    correction: key,
                    original: innerKey,
                    correction_phoneme: values[0],
                    original_phoneme: values[1],
                    distance: values[2],
                    replacements: values[3],
                    correction_type: values[4]
                })

            });
        });

        console.log(result);

        return result;
    }
}

export function correctTranscript(transcript: string, correctWordsList: string[] | null, wordsFindReplace: IWords[] | null, locale: string = 'en'): correctedTranscript {

    let startTime: number = performance.now();

    let initialTranscript: string = transcript;

    let changesMade: any[][] = [];

    //store the words already changed to not change them again in the phonetic correction
    let findAndReplaceWordsUsed = [];

    //Only built when text in a script without spaces shows up
    let wordSegmenter: Intl.Segmenter | undefined;
    const segmentWords = (text: string): Intl.SegmentData[] => Array.from((wordSegmenter ??= new Intl.Segmenter(locale, { granularity: 'word' })).segment(text));

    try {
        //Find and Replace
        if (wordsFindReplace != null) {
            for (let i: number = 0; i < wordsFindReplace.length; i++) {

                const replacementsList: string[] = wordsFindReplace[i].from;

                for (let k: number = 0; k < replacementsList.length; k++) {

                    //\b word boundaries don't work for scripts without spaces, so use the segmenter's word boundaries instead
                    if (NO_SPACE_SCRIPT.test(replacementsList[k])) {
                        const replaced = replaceOnWordBoundaries(transcript, replacementsList[k], wordsFindReplace[i].to, () => segmentWords(transcript), locale);

                        if (replaced.count > 0) {
                            transcript = replaced.text;
                            changesMade.push([wordsFindReplace[i].to, replacementsList[k], "-", "-", -1, replaced.count, "Find"]);
                            findAndReplaceWordsUsed.push(wordsFindReplace[i].to);
                        }
                        continue;
                    }

                    const wordToFind = replacementsList[k].replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
                    const regex: RegExp = new RegExp(`\\b${wordToFind}\\b`, "gi");

                    if (regex.test(transcript)) {

                        const matchCount = (transcript.match(regex) || []).length;

                        transcript = transcript.replaceAll(regex, wordsFindReplace[i].to);
                        changesMade.push([wordsFindReplace[i].to, replacementsList[k], "-", "-", -1, matchCount, "Find"]);
                        findAndReplaceWordsUsed.push(wordsFindReplace[i].to);
                    }
                }
            }
        }

        const wordsListTranscript: string[] = [];
        //Text placed before each word when the transcript is rebuilt: a space between whitespace-separated words,
        //nothing between the words of a chunk written without spaces
        const wordSeparators: string[] = [];
        //The phonetic correction only works on Latin-script words, so words in scripts without spaces and punctuation are skipped
        const skipCorrection: boolean[] = [];

        transcript.split(/\s+/).forEach((chunk, chunkIndex) => {
            if (!NO_SPACE_SCRIPT.test(chunk)) {
                wordsListTranscript.push(chunk);
                wordSeparators.push(chunkIndex > 0 ? ' ' : '');
                skipCorrection.push(false);
                return;
            }

            //A brand name can sit inside the chunk with no spaces around it (明日Salesforseで), so split the chunk into words
            segmentWords(chunk).forEach((word, wordIndex) => {
                wordsListTranscript.push(word.segment);
                wordSeparators.push(chunkIndex > 0 && wordIndex === 0 ? ' ' : '');
                skipCorrection.push(!word.isWordLike || NO_SPACE_SCRIPT.test(word.segment));
            });
        });

        console.log("Transcript after find and replace: " + transcript );

        if (correctWordsList != null) {
            //Phonetic Correction
            const phoneticDictionary: { [key: string]: [string, string] } = {};

            for (let i: number = 0; i < correctWordsList.length; i++) {
                phoneticDictionary[correctWordsList[i]] = doubleMetaphone(correctWordsList[i]);
            }

            for (let i = 0; i < wordsListTranscript.length; i++) {
                if (skipCorrection[i]) continue;

                let currentWord: string = wordsListTranscript[i].replace(/\s+|\x08|\x0C|\x0B|\x07|\0/g, '');

                //Don't use phonetic correction if the word contains numbers
                let numberIncluded = false;

                for (let i = 0; i <= 9; i++) {
                    if (currentWord.includes(String(i))) {
                        numberIncluded = true;
                    }
                }

                let currentWordClean: string = "";
                let currentWordExtensions: string = "";
                const extensions: string[] = [",", ".", "?", '"', ";", "!", ":", "]", "}", "{", "[", "'", "-"];
                let extensionFound: boolean = false;

                //Clean the word
                for (let i: number = 0; i < currentWord.length; i++) {
                    if (extensions.includes(currentWord[i])) {
                        extensionFound = true;
                    }
                    if (extensionFound) {
                        currentWordExtensions = currentWordExtensions + currentWord[i];
                    } else {
                        currentWordClean = currentWordClean + currentWord[i];
                    }

                }
                //if the word doesn't have a number and it wasn´t corrected by the find and replace
                if (!numberIncluded && !findAndReplaceWordsUsed.includes(currentWordClean)) {

                    // REMEDIATE: operates on live speech transcript word
                    currentWordClean = localeLowerCase(currentWordClean, locale);

                    const currentWordPhoneme: [string, string] = doubleMetaphone(currentWordClean);

                    let possibleWordChangesArray: { correctWord: string, distance: number, phoneme: string }[] = [];
                    let sameWordFound: boolean = false;

                    for (let j: number = 0; j < correctWordsList.length; j++) {
                        let correctWord: string = correctWordsList[j];
                        const correctWordPhoneme: [string, string] = phoneticDictionary[correctWord];

                        // REMEDIATE: correctWord from caller-supplied reference word list, not a fixed constant
                        if (currentWordClean == localeLowerCase(correctWord, locale)) {

                            correctWord = correctWord + currentWordExtensions;

                            wordsListTranscript[i] = correctWord;
                            sameWordFound = true;

                            break;
                        }

                        //Calculate the distance between the phonetic key of the current word and the phonetic key of the "correct word".
                        let distance: number = phoneticKeyDifference(currentWordPhoneme[0], correctWordPhoneme[0]);

                        //If the word we are trying to replace is not in the dictionary or the distance is 0 and the phonetic key has more than 2 letters, we add that correct word to the list of possible changes.
                        if (((distance <= 1 && !dictionary.includes(currentWordClean)) || (distance === 0 && currentWordPhoneme.length > 2))) {

                            possibleWordChangesArray.push({ correctWord: correctWord, distance: distance, phoneme: correctWordPhoneme[0] });

                        }
                    }
                    if (possibleWordChangesArray.length > 0 && !sameWordFound) {
                        let correctWordChoosen: string = possibleWordChangesArray[0].correctWord;
                        let distanceChoosen: number = possibleWordChangesArray[0].distance;
                        let correctWordChoosenPhoneme: string = possibleWordChangesArray[0].phoneme;

                        if (possibleWordChangesArray.length > 1) {

                            let difference: number = auxiliarDifference(correctWordChoosenPhoneme, currentWordPhoneme[0]);

                            for (let i: number = 1; i < possibleWordChangesArray.length; i++) {
                                let currentDifference: number = auxiliarDifference(possibleWordChangesArray[i].phoneme, currentWordPhoneme[0]);
                                if (currentDifference < difference) {
                                    correctWordChoosen = possibleWordChangesArray[i].correctWord;
                                    distanceChoosen = possibleWordChangesArray[i].distance;
                                    correctWordChoosenPhoneme = possibleWordChangesArray[i].phoneme;
                                }
                            }

                        }
                        changesMade.push([correctWordChoosen, currentWordClean, correctWordChoosenPhoneme, currentWordPhoneme[0], distanceChoosen, 1, "Phonetic"]);

                        correctWordChoosen = correctWordChoosen + currentWordExtensions;

                        wordsListTranscript[i] = correctWordChoosen;

                    }
                }
            }

        }

        let finalTranscript: string = wordsListTranscript.map((word, i) => wordSeparators[i] + word).join('');


        var endTime: number = performance.now();

        let response: correctedTranscript = new correctedTranscript(finalTranscript, changesMade);

        console.log(`Call to correctTranscript took ${Math.round(endTime - startTime)} milliseconds`);

        return response;

    } catch (err) {
        const logger = inject(LoggerService);
        logger.error('Error correcting transcript', err);
        return new correctedTranscript(initialTranscript, []);
    }
}

//function to calculate the distance between the phonetic keys
export function phoneticKeyDifference(a: string, b: string): number {
    let str1: string = a;
    let str2: string = b;

    let count: number = 0;

    if (a.length < b.length) {
        str1 = b;
        str2 = a;
    }

    let j: number = 0;

    for (let i: number = 0; i < str1.length; i++) {
        if (j < str2.length) {
            if (str1[i] != str2[j]) {
                count = count + 1;
                if ((i + 1) < str1.length) {
                    if (str1[i + 1] != str2[j]) {
                        j = j + 1;
                    } else {
                        if ((j + 1) < str2.length) {
                            if (str1[i] == str2[j + 1]) {
                                i = i + 1;
                                j = j + 2;
                            }
                        }
                    }
                }
            } else {
                j = j + 1;
            }

        } else {
            count = count + 1;
            j = j + 1;
        }
    }
    return count;
}

export function auxiliarDifference(a: string, b: string): number {
    let str1: string = a;
    let str2: string = b;

    let count: number = 0;

    if (a.length < b.length) {
        str1 = b;
        str2 = a;
    }

    for (let i: number = 0; i < str1.length; i++) {
        if (i < str2.length) {
            if (str1[i] != str2[i]) {
                count = count + Math.pow(10, (str1.length - 1) - i);
            }
        }
    }

    return count;

}

